import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Alert, Box, Button, Stack, Typography } from '@mui/material'

import { useGetBlueprintFamilyEventTypeDurationDistributionQuery } from '../../api/blueprintFamilies/durationDistributionEventTypes'
import { useGetBlueprintFamilyMomentumQuery } from '../../api/blueprintFamilies/momentum'
import { useGetBlueprintFamilyRollupQuery } from '../../api/blueprintFamilies/rollup'
import { useGetBlueprintFamilyEventTypeTrendsQuery } from '../../api/blueprintFamilies/trendsEventTypes'
import { useGetBlueprintFamiliesQuery } from '../../api/core'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { BlueprintHierarchyBar, BlueprintHierarchyMenus } from '../../features/market-analytics/components/BlueprintHierarchyBar/BlueprintHierarchyBar'
import { BlueprintTrendsChart } from '../../features/market-analytics/components/BlueprintTrendsChart'
import { DetailPageMessageCard } from '../../features/market-analytics/components/DetailPageMessageCard/DetailPageMessageCard'
import { EventTypeBlueprintRow, EventTypeBlueprintsCard } from '../../features/market-analytics/components/EventTypeBlueprintsCard/EventTypeBlueprintsCard'
import { GlossaryDescriptionCard } from '../../features/market-analytics/components/GlossaryDescriptionCard/GlossaryDescriptionCard'
import { LevelStatsCard, densityStatsOf } from '../../features/market-analytics/components/LevelStatsCard/LevelStatsCard'
import { LevelTrendsChart, LevelTrendsChartSplit } from '../../features/market-analytics/components/LevelTrendsChart/LevelTrendsChart'
import { MarketAnalyticsFilterBar } from '../../features/market-analytics/components/MarketAnalyticsFilterBar'
import { NewAdoptersModal } from '../../features/market-analytics/components/NewAdoptersModal'
import { TypicalDurationsCard } from '../../features/market-analytics/components/TypicalDurationsCard/TypicalDurationsCard'
import {
  adoptingGamesOf,
  familyMembershipInEventType,
  buildEventCategoryPath,
  buildEventTypePath,
  filterCatalogueToScope,
  rollupRowAdoption,
  resolveCohortSize,
  buildBlueprintFamilyPath,
  BLUEPRINT_FAMILY_CHART_TITLE_KEYS,
} from '../../features/market-analytics/components/helpers/catalogueUtils'
import { toDurationBuckets } from '../../features/market-analytics/components/helpers/durationBuckets'
import { getEventTypeDescription } from '../../features/market-analytics/components/helpers/eventTypeGlossary'
import { buildHierarchySiblings } from '../../features/market-analytics/components/helpers/hierarchySiblings'
import { getTranslatedName } from '../../features/market-analytics/components/helpers/metricUtils'
import { toNewAdopterGames } from '../../features/market-analytics/components/helpers/newAdopterGames'
import { selectedPeriodFor } from '../../features/market-analytics/components/helpers/trendsSelectedPeriod'
import { useBlueprintFamilyDescriptions } from '../../features/market-analytics/hooks/useBlueprintFamilyDescriptions'
import { useCatalogueScope, useCatalogueTaxonomyCounts } from '../../features/market-analytics/hooks/useCatalogueScope'
import { useNewAdopterGameIcons } from '../../features/market-analytics/hooks/useNewAdopterGameIcons'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import languageService from '../../services/LanguageService'
import PageService from '../../services/PageService'
import { MarketEventTypeDetailPageSkeleton } from './MarketEventTypeDetailPageSkeleton'

/**
 * Event-type detail page: the level between the catalogue and a single
 * blueprint. Shows the type's segment-scoped adoption stats from
 * `rollup?groupBy=EVENT_TYPE` and the blueprints it contains, each linking down
 * to its own page.
 *
 * Every type-level figure here needs a server-side union across the type's
 * blueprint families — summing them client-side double-counts a game that
 * adopted more than one — so the total trend line, Change, Density and the
 * duration buckets each come from their own grouped endpoint rather than from
 * the families listed below. They were all a dash until Live Event Analytics
 * 1.0 shipped those endpoints (handoff C1–C3). A games drill-down still has
 * none and is left visibly missing rather than approximated.
 */
const MarketEventTypeDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { eventTypeId } = useParams<{ eventTypeId: string }>()
  const userLanguage = useCurrentUserLanguage()

  usePage(PageService.getPageWithId('market-analytics-event-type'), 'Visited Market Analytics Event Type Detail')

  const skip = !eventTypeId

  // Same URL-backed filter/window state as the catalogue and the blueprint page,
  // so filters carried in on a crumb link parse natively here too. `navState` is
  // passed on by every link out of this page, so the next page's root crumb still
  // returns to the same catalogue view.
  const {
    state,
    tagGroupInfos,
    windowDays,
    selectedCategoryIds,
    selectedGenreIds,
    selectedSubgenreIds,
    effectiveSubgenreIds,
    conventionalSubgenreIds,
    scope,
    isUnscoped,
    carriedSearch,
    rootCrumbSearch,
    crumbNavState: navState,
  } = useCatalogueScope()

  // ---- Data ------------------------------------------------------------------
  // Catalogue list: the type's child blueprints, its parent category and the
  // hierarchy bar's sibling inventory. Same args as the catalogue and blueprint
  // pages, so navigating between them hits the RTK cache. Crumbs and menus read
  // `data` so they don't blink on a segment change; the blueprint table reads
  // `currentData`, so the previous window's adopter counts are never divided by
  // this window's cohort.
  const {
    data: catalogueList,
    currentData: currentCatalogueList,
    isLoading: isCatalogueLoading,
    isFetching: isCatalogueFetching,
    isError: isCatalogueError,
    refetch: refetchCatalogue,
  } = useGetBlueprintFamiliesQuery({ windowDays, ...scope })
  // Taxonomy picker counts, from the unscoped snapshot as on the catalogue page.
  const { allBlueprints, taxonomyCounts } = useCatalogueTaxonomyCounts(catalogueList, isUnscoped)

  // Per-event-type aggregates (games deduplicated across the type's blueprints
  // server-side — the reason this page doesn't compute them from the list).
  const {
    currentData: typeRollup,
    isFetching: isTypeRollupFetching,
    isError: isTypeRollupError,
    refetch: refetchTypeRollup,
  } = useGetBlueprintFamilyRollupQuery({ groupBy: 'EVENT_TYPE', windowDays, ...scope }, { skip })

  // The Change tile. Event types had no change figure at all before the momentum
  // endpoint gained `groupBy`; the only alternative was summing the momentum of the
  // type's families, which double-counts a game that adopted two of them.
  const {
    currentData: typeMomentum,
    isFetching: isTypeMomentumFetching,
    isError: isTypeMomentumError,
    refetch: refetchTypeMomentum,
  } = useGetBlueprintFamilyMomentumQuery({ groupBy: 'EVENT_TYPE', windowDays, ...scope }, { skip })
  const typeMomentumRow = useMemo(() => (typeMomentum ?? []).find((row) => row.id === eventTypeId), [typeMomentum, eventTypeId])
  // The type's own trend series, games deduplicated across its blueprints server-side.
  // Feeds the chart's total line and the Density tile — both of which used to be a dash
  // here, because this endpoint did not exist before Live Event Analytics 1.0.
  const {
    currentData: typeTrendsItems,
    isFetching: isTypeTrendsFetching,
    isError: isTypeTrendsError,
    refetch: refetchTypeTrends,
  } = useGetBlueprintFamilyEventTypeTrendsQuery({ eventTypeIds: eventTypeId ? [eventTypeId] : [], windowDays, ...scope }, { skip: !eventTypeId })
  const typeTrends = useMemo(() => (typeTrendsItems ?? []).find((item) => item.id === eventTypeId), [typeTrendsItems, eventTypeId])
  const { densityScalar } = useMemo(() => densityStatsOf(typeTrends?.density), [typeTrends?.density])

  // The months the chart plots: the selected period's, which is what `typeTrends`
  // covers. The Density tile reads the same response.
  const selectedPeriod = useMemo(() => selectedPeriodFor(windowDays), [windowDays])

  // Typical durations, type-scoped: how long games run events of this type, across every
  // blueprint family filed under it. Summing the families' own histograms would count a
  // game once per family it adopted, so this waited for the grouped endpoint (handoff C3).
  const {
    currentData: durationItems,
    isFetching: isDurationFetching,
    isError: isDurationError,
    refetch: refetchDuration,
  } = useGetBlueprintFamilyEventTypeDurationDistributionQuery({ eventTypeIds: eventTypeId ? [eventTypeId] : [], windowDays, ...scope }, { skip: !eventTypeId })
  const durationRow = useMemo(() => (durationItems ?? []).find((item) => item.id === eventTypeId), [durationItems, eventTypeId])
  const durationBuckets = useMemo(() => toDurationBuckets(durationRow?.buckets, t), [durationRow, t])

  const typeRow = useMemo(() => (typeRollup ?? []).find((row) => row.groupId === eventTypeId), [typeRollup, eventTypeId])
  // This page is about the event type itself, so its tile counts every game running any
  // event of it — not only those whose blueprint was catalogued into a family.
  const typeAdoption = useMemo(() => rollupRowAdoption(typeRow), [typeRow])

  // The request's cohort — the adoption denominator, read the same way as on the
  // catalogue. The type's own rollup row states it; the EVENT_GROUP rollup is the
  // fallback (and the catalogue's shared cache entry).
  const {
    currentData: groupRollup,
    isFetching: isGroupRollupFetching,
    isError: isGroupRollupError,
    refetch: refetchGroupRollup,
  } = useGetBlueprintFamilyRollupQuery({ groupBy: 'EVENT_GROUP', windowDays, ...scope })
  const cohortSize = useMemo(() => typeRow?.cohortGamesCount ?? resolveCohortSize(groupRollup ?? []), [typeRow, groupRollup])

  // The games that FIRST ran this event type inside the window, named by the backend.
  // Their icons ride on the entries; the tracked-games list fills in for backends predating GR-3245.
  const { iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId, isLoading: isGameIconsLoading } = useNewAdopterGameIcons()
  const newAdopterGames = useMemo(
    () => toNewAdopterGames(typeRow?.newGames, { userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId }),
    [typeRow?.newGames, userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId]
  )

  // Remembers which event type the modal was opened for, so moving to a sibling via the hierarchy bar
  // (same route, same component) doesn't carry an open modal over to the next one.
  const [newAdoptersOpenFor, setNewAdoptersOpenFor] = useState<string | null>(null)
  const isNewAdoptersModalOpen = Boolean(eventTypeId) && newAdoptersOpenFor === eventTypeId
  const openNewAdoptersModal = useCallback(() => setNewAdoptersOpenFor(eventTypeId ?? null), [eventTypeId])
  const closeNewAdoptersModal = useCallback(() => setNewAdoptersOpenFor(null), [])
  // The page stays mounted when only the id changes, so going on to another one and back must not reopen it.
  useEffect(() => {
    setNewAdoptersOpenFor(null)
  }, [eventTypeId])

  // ---- Derived display values --------------------------------------------------
  const eventTypeName = eventTypeId ? languageService.getTranslation('tags', eventTypeId) || eventTypeId : ''
  // Same glossary copy the Data Glossary's Live Event Types tab shows. Comes
  // from the language map loaded before the app renders, so it needs no loading
  // state of its own.
  const eventTypeDescription = eventTypeId ? getEventTypeDescription(eventTypeId) : ''

  // The parent category comes from the type's blueprints (the tag → tag-group
  // mapping the list carries): the scoped list first, then, while a segment is active,
  // the unscoped snapshot, so a narrow segment filter can't erase the crumb or turn the
  // page into "not found". The snapshot is the request the taxonomy counts already
  // make, so this adds none.
  // NOTE: open question for the backend — whether the list's `eventTypes` depend on the
  // segment at all. The snapshot is also on the default window rather than `windowDays`;
  // a type's category doesn't change with either, so the fallback is safe.
  const categoryId = useMemo(() => {
    for (const list of [catalogueList, allBlueprints]) {
      for (const bp of list ?? []) {
        const membership = familyMembershipInEventType(bp, eventTypeId ?? '')
        if (membership) return membership.categoryId
      }
    }
    return ''
  }, [catalogueList, allBlueprints, eventTypeId])
  const categoryName = categoryId ? languageService.getTranslation('tagGroups', categoryId) || categoryId : ''
  const categoryColorHex = useMemo(() => tagGroupInfos.find((group) => group.id === categoryId)?.colorHex, [tagGroupInfos, categoryId])

  // Glossary copy for the blueprint table's name hover cards.
  const blueprintDescriptions = useBlueprintFamilyDescriptions(userLanguage)

  useDocumentTitle(t('sidebar:market_analytics') + (eventTypeName ? `: ${eventTypeName}` : ''))

  // Segment-scoped inventory, matching how the blueprint page scopes its menus.
  const scopedCatalogueList = useMemo(() => filterCatalogueToScope(catalogueList, effectiveSubgenreIds), [catalogueList, effectiveSubgenreIds])

  const currentScopedCatalogueList = useMemo(
    () => filterCatalogueToScope(currentCatalogueList, effectiveSubgenreIds),
    [currentCatalogueList, effectiveSubgenreIds]
  )

  // Child blueprints of this type, in the current segment. A family belongs to
  // every allowed event type its events carried in the window (`eventTypes`,
  // handoff C4), not only its most-used one.
  const blueprintRows = useMemo<EventTypeBlueprintRow[]>(() => {
    return (currentScopedCatalogueList ?? [])
      .filter((bp) => familyMembershipInEventType(bp, eventTypeId ?? '') !== null)
      .map((bp) => {
        // The family's own count over the period's last month, the same figure the
        // chart's last point and the blueprint family page show. Deliberately not the
        // per-type `eventTypes[].distinctGamesCount`, which leaves out games that ran a
        // multi-type family only under its other types, so it would read lower than
        // both. Null when the backend omits the field, since the all-time `gamesCount`
        // is a different scale.
        const adoptingGames = adoptingGamesOf(bp)
        return {
          id: bp.id,
          name: getTranslatedName(bp.names, userLanguage),
          to: buildBlueprintFamilyPath(bp.id, carriedSearch),
          state: navState,
          discontinued: bp.discontinued,
          adoptionPct: adoptingGames !== null && cohortSize && cohortSize > 0 ? adoptingGames / cohortSize : bp.adoptionRate?.windowed ?? null,
          adoptingGames,
          newAdopters: bp.newGames?.length ?? null,
          description: blueprintDescriptions.get(bp.id),
        }
      })
  }, [currentScopedCatalogueList, eventTypeId, userLanguage, carriedSearch, navState, cohortSize, blueprintDescriptions])

  // Chart series: the same child blueprints, most-adopted first, since the chart
  // keeps only the first ten (endpoint cap) and those are the ones worth the
  // space. `adoptionPct` can be null on either side, which sorts last.
  const chartItems = useMemo(
    () =>
      [...blueprintRows]
        .sort((a, b) => (b.adoptionPct ?? -1) - (a.adoptionPct ?? -1))
        .map((row) => ({ id: row.id, name: row.name, description: row.description })),
    [blueprintRows]
  )

  // Gated on the series, not just the id: an entity whose scope matches no tracked game
  // is omitted from the response rather than zero-filled, and a present-but-sourceless
  // total renders as an empty chart instead of falling back to the split view.
  // Memoized, like `trendsSplit`, because both feed the memoized chart card.
  const trendsTotal = useMemo(
    () => (eventTypeId && typeTrends ? { item: { id: eventTypeId, name: eventTypeName }, source: typeTrends, period: selectedPeriod ?? undefined } : null),
    [eventTypeId, eventTypeName, typeTrends, selectedPeriod]
  )
  const isTrendsTotalLoading = isTypeTrendsFetching && !typeTrendsItems
  const isSplitLoading = isCatalogueFetching && chartItems.length === 0
  const splitLabel = t('market-analytics:trend_split_by_blueprints')
  const splitTitle = t(BLUEPRINT_FAMILY_CHART_TITLE_KEYS[state.listSort])
  const trendsSplit = useMemo<LevelTrendsChartSplit>(
    () => ({
      label: splitLabel,
      render: ({ tabs, headerControls, footer, embedded }) => (
        <BlueprintTrendsChart
          items={chartItems}
          metric={state.listSort}
          windowDays={windowDays}
          conventionalSubgenreIds={conventionalSubgenreIds}
          isLoading={isSplitLoading}
          tabs={tabs}
          headerControls={headerControls}
          footer={footer}
          title={splitTitle}
          hideSubtitle
          embedded={embedded}
        />
      ),
    }),
    [splitLabel, splitTitle, chartItems, state.listSort, windowDays, conventionalSubgenreIds, isSplitLoading]
  )

  // Sibling switchers: categories move between category pages, event types
  // between event-type pages, and the blueprint level — which this page can't
  // name, since it sits above it — is a placeholder whose menu drops straight
  // into one of the type's blueprints.
  const hierarchyMenus = useMemo<BlueprintHierarchyMenus>(() => {
    const siblings = buildHierarchySiblings(scopedCatalogueList, { categoryId, eventTypeId, userLanguage, tagGroups: tagGroupInfos })

    return {
      eventCategory: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_categories'),
        options: siblings.eventCategories.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          isCurrent: sibling.id === categoryId,
          to: buildEventCategoryPath(sibling.id, carriedSearch),
          state: navState,
          colorHex: sibling.colorHex,
        })),
      },
      eventType: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_types'),
        options: siblings.eventTypes.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          isCurrent: sibling.id === eventTypeId,
          to: buildEventTypePath(sibling.id, carriedSearch),
          state: navState,
          colorHex: sibling.colorHex,
        })),
      },
      blueprint: {
        ariaLabel: t('market-analytics:hierarchy_menu_blueprints'),
        options: siblings.blueprints.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          // None of them is being viewed — this page sits one level above.
          isCurrent: false,
          to: buildBlueprintFamilyPath(sibling.id, carriedSearch),
          state: navState,
          colorHex: sibling.colorHex,
        })),
      },
    }
  }, [scopedCatalogueList, categoryId, eventTypeId, userLanguage, carriedSearch, navState, tagGroupInfos, t])

  // A failed section request would otherwise read as a dash, a hidden card or an empty
  // chart. The event-group rollup only backs the cohort, so its failure counts only when
  // the type's own row didn't state one. Retry re-runs every request that failed.
  const hasSectionLoadError = isTypeRollupError || isTypeMomentumError || isTypeTrendsError || isDurationError || (isGroupRollupError && cohortSize === null)
  const retryFailedRequests = useCallback(() => {
    if (isCatalogueError) refetchCatalogue()
    if (isTypeRollupError) refetchTypeRollup()
    if (isTypeMomentumError) refetchTypeMomentum()
    if (isTypeTrendsError) refetchTypeTrends()
    if (isDurationError) refetchDuration()
    if (isGroupRollupError) refetchGroupRollup()
  }, [
    isCatalogueError,
    refetchCatalogue,
    isTypeRollupError,
    refetchTypeRollup,
    isTypeMomentumError,
    refetchTypeMomentum,
    isTypeTrendsError,
    refetchTypeTrends,
    isDurationError,
    refetchDuration,
    isGroupRollupError,
    refetchGroupRollup,
  ])

  // Only the catalogue list gates the first render (names + children come from
  // it); filter/window changes refetch in place with per-section loading.
  if (isCatalogueLoading) {
    return (
      <MarketEventTypeDetailPageSkeleton
        selectedCategoryCount={selectedCategoryIds.length}
        selectedGenreCount={selectedGenreIds.length}
        selectedSubgenreCount={selectedSubgenreIds.length}
      />
    )
  }

  // A failed list leaves the type with no parent category and no blueprints, which
  // would read as "not found" (or an empty table). `catalogueList` may still hold the
  // previous segment's list after a failed refetch, so the error is checked first.
  if (isCatalogueError) {
    return (
      <DetailPageMessageCard
        title={t('market-analytics:catalogue_load_error')}
        description={t('market-analytics:catalogue_load_error_description')}
        backTo={`/live-event-analytics${rootCrumbSearch}`}
        onRetry={retryFailedRequests}
      />
    )
  }

  // An unknown tag id has neither a translated name nor blueprints anywhere in
  // the catalogue — the id is wrong (or the type was retired), so say so instead
  // of rendering an empty page.
  const isUnknownEventType = !eventTypeId || (!categoryId && !languageService.getTranslation('tags', eventTypeId))
  if (isUnknownEventType) {
    return (
      <DetailPageMessageCard
        title={t('market-analytics:event_type_not_found')}
        description={t('market-analytics:event_type_not_found_description', { id: eventTypeId })}
        backTo={`/live-event-analytics${rootCrumbSearch}`}
      />
    )
  }

  return (
    <Box>
      {/* Hierarchy bar: back to catalog › category › this event type › the
          blueprint level as a placeholder you can drop into */}
      <BlueprintHierarchyBar
        categoryName={categoryName}
        eventTypeName={eventTypeName}
        catalogueSearch={rootCrumbSearch}
        menus={hierarchyMenus}
        links={categoryId ? { eventCategory: buildEventCategoryPath(categoryId, carriedSearch) } : undefined}
        linkState={navState}
      />

      {/* Segment filter: same cascading chips as the catalogue, in the same spot
          (directly under the hierarchy bar) so a selection carried over from the
          catalogue is where the user left it instead of buried between cards. */}
      <Box sx={{ mt: 1.5 }}>
        <MarketAnalyticsFilterBar
          selectedCategoryIds={selectedCategoryIds}
          selectedGenreIds={selectedGenreIds}
          selectedSubgenreIds={selectedSubgenreIds}
          onSetCategories={state.setCategories}
          onSetGenres={state.setGenres}
          onSetSubgenres={state.setSubgenres}
          onClearCategories={state.clearCategories}
          onClearGenres={state.clearGenres}
          onClearSubgenres={state.clearSubgenres}
          onClearTaxonomy={state.clearTaxonomy}
          taxonomyCounts={taxonomyCounts}
          cohortGamesCount={cohortSize}
        />
      </Box>

      {/* Header: the type's name only. The generic "what an event type is"
          sentence that used to sit here said less than the description card
          below it, and reading both back to back was redundant. */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h2" component="h1" sx={{ fontWeight: 'bold', lineHeight: 1.2, wordBreak: 'break-word' }}>
          {eventTypeName}
        </Typography>
      </Box>

      {/* Single full-width column — the sticky "other event types" rail was
          dropped in favour of the hierarchy bar's sibling dropdowns. */}
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        {hasSectionLoadError ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={retryFailedRequests}>
                {t('market-analytics:retry')}
              </Button>
            }
          >
            {t('market-analytics:detail_partial_load_error')}
          </Alert>
        ) : null}

        {/* Leads the column for the same reason it does on the blueprint page:
            what the type is comes before how widely it is adopted. */}
        <GlossaryDescriptionCard kind="eventType" description={eventTypeDescription} />

        {/* Same card and tiles as the blueprint and category pages, and now with the
            same data behind them: `trends/event-types` gives the type its own total
            line and Density tile, where both were a dash before.

            The Adoption tile counts games running any event of the type; the
            blueprint-families table below it counts each family's own events. */}
        <LevelStatsCard
          kind="eventType"
          window={state.window}
          onWindowChange={state.setWindow}
          adoptingGames={typeAdoption.adoptingGames}
          cohortSize={cohortSize ?? typeMomentumRow?.cohortGamesCount ?? null}
          adoptionRateFallback={typeAdoption.adoptionRate}
          deltaGames={typeMomentumRow?.momentum ?? null}
          isChangeLoading={isTypeMomentumFetching && !typeMomentumRow}
          densityScalar={densityScalar}
          newAdopters={typeRow?.newGames?.length ?? null}
          newAdopterGames={newAdopterGames}
          onNewAdoptersClick={openNewAdoptersModal}
          isAdoptionLoading={isTypeRollupFetching || isGroupRollupFetching}
          isNewAdoptersLoading={(isTypeRollupFetching && !typeRow) || isGameIconsLoading}
          isDensityLoading={isTypeTrendsFetching && !typeTrends}
          chart={
            <LevelTrendsChart
              metric={state.listSort}
              onMetricChange={state.setListSort}
              nameColumnHeader={t('market-analytics:market_level_blueprint_name')}
              total={trendsTotal}
              isTotalLoading={isTrendsTotalLoading}
              isTotalError={isTypeTrendsError && !typeTrendsItems}
              onTotalRetry={refetchTypeTrends}
              split={trendsSplit}
              cohortSize={cohortSize}
              embedded
            />
          }
        />

        {/* Type-scoped durations histogram; hidden when every bucket is empty, skeleton
            while the fetch resolves — same as on the blueprint page. */}
        <TypicalDurationsCard
          buckets={durationBuckets}
          description={t('market-analytics:durations_description_event_type')}
          isLoading={isDurationFetching && !durationRow}
        />

        <EventTypeBlueprintsCard
          rows={blueprintRows}
          isLoading={isCatalogueFetching && !currentCatalogueList}
          categoryName={categoryName}
          eventTypeName={eventTypeName}
          categoryColorHex={categoryColorHex}
        />
      </Stack>
      <NewAdoptersModal
        open={isNewAdoptersModalOpen}
        onClose={closeNewAdoptersModal}
        games={newAdopterGames}
        isLoading={(isTypeRollupFetching && !typeRow) || isGameIconsLoading}
        kind="eventType"
        entityName={eventTypeName}
        window={state.window}
      />
    </Box>
  )
}

export default MarketEventTypeDetailPage
